import { keyframes, styled } from "@mui/material"
import { useCallback, useEffect, useRef, useState } from "react"

const slideDown = keyframes`
    from {transform: translateY(-200%)}
    to {transform: translateY(0)}
`

interface OuterBoxProps {
  componentHeight: number
  fixed: boolean
}

const OuterBox = styled("div")<OuterBoxProps>(({ componentHeight, fixed }) => ({
  "& + .section-after-sticky": {
    paddingTop: fixed ? componentHeight : 0,
  },
}))

interface InnerBoxProps {
  fixedOn: number
  fixed: boolean
}

const InnerBox = styled("div")<InnerBoxProps>(({ theme, fixedOn, fixed }) => ({
  ...(!fixed && {
    zIndex: 5,
    boxShadow: "none",
    position: "relative",
  }),
  ...(fixed && {
    left: 0,
    right: 0,
    zIndex: 500,
    position: "fixed",
    top: `${fixedOn}px`,
    transition: "all 350ms ease-in-out",
    animation: `${slideDown} 400ms ${theme.transitions.easing.easeInOut}`,
  }),
}))

type StickyProps = {
  fixedOn: number
  children: React.ReactNode
  onSticky?: (fixed: boolean) => void
  scrollDistance?: number
}

const Sticky = ({ fixedOn, children, onSticky, scrollDistance = 0 }: StickyProps) => {
  const [fixed, setFixed] = useState(false)
  const [parentHeight, setParentHeight] = useState(0)
  const elementRef = useRef<HTMLDivElement>(null)
  const positionRef = useRef(0)

  const scrollListener = useCallback(() => {
    if (!window) return

    // Distance of element from window top (-) minus value
    const distance = window.pageYOffset - positionRef.current

    let isFixed = distance >= fixedOn + scrollDistance
    if (positionRef.current === 0) {
      isFixed = distance >= fixedOn + (elementRef.current?.offsetHeight || 0) + scrollDistance
    }
    setFixed(isFixed)
  }, [fixedOn, scrollDistance])

  useEffect(() => {
    if (!window) return
    window.addEventListener("scroll", scrollListener)
    window.addEventListener("resize", scrollListener)
    return () => {
      window.removeEventListener("scroll", scrollListener)
      window.removeEventListener("resize", scrollListener)
    }
  }, [scrollListener])

  useEffect(() => {
    if (!positionRef.current) {
      positionRef.current = elementRef.current?.offsetTop || 0
    }
    setParentHeight(elementRef.current?.offsetHeight || 0)
  }, [children])

  useEffect(() => {
    if (onSticky) onSticky(fixed)
  }, [fixed, onSticky])

  return (
    <OuterBox componentHeight={parentHeight} fixed={fixed}>
      <InnerBox fixedOn={fixedOn} fixed={fixed} ref={elementRef}>
        {children}
      </InnerBox>
    </OuterBox>
  )
}
export default Sticky
